import {
  ActivitySquareIcon,
  BarChart2Icon,
  BlocksIcon,
  BotIcon,
  BugIcon,
  Building2Icon,
  BuildingIcon,
  CalendarCogIcon,
  CalendarIcon,
  ClipboardListIcon,
  CreditCardIcon,
  DatabaseZapIcon,
  FileTextIcon,
  FlaskConicalIcon,
  HandPlatterIcon,
  HomeIcon,
  LayoutDashboardIcon,
  ListTodoIcon,
  LockIcon,
  LucideIcon,
  MapPinIcon,
  MegaphoneIcon,
  PhoneCallIcon,
  RefreshCcwIcon,
  RouteIcon,
  SendHorizontalIcon,
  SettingsIcon,
  SprayCanIcon,
  StickyNoteIcon,
  TargetIcon,
  ToggleRightIcon,
  TrophyIcon,
  User2Icon,
  UserIcon,
  Users2Icon,
  UsersIcon,
  Volume2Icon,
  WrenchIcon,
} from 'lucide-react';

import { ModuleName } from '@/components/providers/modules-provider';

export interface MenuSubItem {
  key: string;
  label: string;
  icon: LucideIcon;
  url: string;
  requiredRole?: 'superuser' | 'admin';
  requiredModule?: ModuleName;
}

export interface MenuItem {
  key: string;
  label: string;
  icon: LucideIcon;
  url?: string;
  items?: Record<string, MenuSubItem>;
  requiredRole?: 'superuser' | 'admin';
  requiredModule?: ModuleName;
}

export const NAV_CONFIG = {
  home: {
    key: 'home',
    label: 'Home',
    icon: HomeIcon,
    url: '/',
  },
  customers: {
    key: 'customers',
    label: 'Customers',
    icon: User2Icon,
    url: '/customers',
    requiredModule: 'customers',
  },
  routes: {
    key: 'routes',
    label: 'Routes',
    icon: RouteIcon,
    url: '/routes',
    requiredModule: 'settings',
  },
  scheduling: {
    key: 'scheduling',
    label: 'Scheduling',
    icon: CalendarIcon,
    requiredModule: 'scheduling',
    items: {
      jobPool: {
        key: 'jobPool',
        url: '/scheduling/job-pool',
        label: 'Job Pool',
        icon: ListTodoIcon,
      },
      schedules: {
        key: 'schedules',
        url: '/scheduling/schedules',
        label: 'Schedules',
        icon: ClipboardListIcon,
      },
    },
  },
  voiceAi: {
    key: 'voiceAi',
    label: 'Voice AI',
    icon: BotIcon,
    items: {
      voiceAiHome: {
        key: 'voiceAiHome',
        label: 'Dashboard',
        icon: LayoutDashboardIcon,
        url: '/voice-agent/dashboard',
        requiredModule: 'voice-agent:calls',
      },
      voiceAiCalls: {
        key: 'voiceAiCalls',
        label: 'Calls',
        icon: PhoneCallIcon,
        url: '/calls',
        requiredModule: 'voice-agent:calls',
      },
      voiceAiSetup: {
        key: 'voiceAiSetup',
        label: 'Set-up',
        icon: SettingsIcon,
        url: '/voice-agent',
        requiredModule: 'voice-agent:settings',
      },
      voiceAiVoices: {
        key: 'voiceAiVoices',
        url: '/voice-agent/voices',
        label: 'Voices',
        icon: Volume2Icon,
        requiredModule: 'voice-agent:voices',
      },
    },
  },
  callCenter: {
    key: 'callCenter',
    label: 'Call Center',
    icon: Users2Icon,
    requiredModule: 'external-calls',
    items: {
      analyticsDashboard: {
        key: 'analyticsDashboard',
        url: '/analytics/dashboard',
        label: 'Dashboard',
        icon: LayoutDashboardIcon,
        requiredModule: 'external-calls:dashboard',
      },
      analyticsCallAnalytics: {
        key: 'analyticsCallAnalytics',
        url: '/analytics/call-analytics',
        label: 'Call Analytics',
        icon: BarChart2Icon,
      },
      analyticsAiCoach: {
        key: 'analyticsAiCoach',
        url: '/analytics/ai-coach',
        label: 'Leaderboard',
        icon: TrophyIcon,
      },
    },
  },
  marketing: {
    key: 'marketing',
    label: 'Marketing',
    icon: MegaphoneIcon,
    requiredModule: 'marketing',
    items: {
      marketingNotifications: {
        key: 'marketingNotifications',
        url: '/marketing/notifications',
        label: 'Notifications',
        icon: SendHorizontalIcon,
      },
      marketingTriggers: {
        key: 'marketingTriggers',
        url: '/marketing/triggers',
        label: 'Triggers',
        icon: CalendarCogIcon,
      },
      marketingTemplates: {
        key: 'marketingTemplates',
        url: '/marketing/templates',
        label: 'Templates',
        icon: StickyNoteIcon,
      },
    },
  },
  billing: {
    key: 'billing',
    label: 'Billing',
    icon: CreditCardIcon,
    requiredModule: 'billing',
    items: {
      billingInvoices: {
        key: 'billingInvoices',
        url: '/invoices',
        label: 'Invoices',
        icon: FileTextIcon,
      },
    },
  },
  settings: {
    key: 'settings',
    label: 'Settings',
    icon: SettingsIcon,
    requiredModule: 'settings',
    items: {
      settingsOffice: {
        key: 'settingsOffice',
        url: '/settings/office',
        label: 'Office',
        icon: Building2Icon,
      },
      settingsServiceAreas: {
        key: 'settingsServiceAreas',
        url: '/settings/service-areas',
        label: 'Service Areas',
        icon: MapPinIcon,
      },

      settingsIssues: {
        key: 'settingsIssues',
        url: '/settings/issues',
        label: 'Issues',
        icon: BugIcon,
      },
      settingsSkills: {
        key: 'settingsSkills',
        url: '/settings/skills',
        label: 'Skills',
        icon: WrenchIcon,
      },
      settingsEmployees: {
        key: 'settingsEmployees',
        url: '/settings/employees',
        label: 'Employees',
        icon: UserIcon,
      },
      settingsTeams: {
        key: 'settingsTeams',
        url: '/settings/teams',
        label: 'Teams',
        icon: UsersIcon,
      },
      settingsServiceTypes: {
        key: 'settingsServiceTypes',
        url: '/settings/service_types',
        label: 'Service Types',
        icon: HandPlatterIcon,
      },
      settingsBilling: {
        key: 'settingsBilling',
        url: '/settings/billing',
        label: 'Billing',
        icon: CreditCardIcon,
        requiredModule: 'billing',
      },
      settingsServicePlans: {
        key: 'settingsServicePlans',
        url: '/settings/service_plans',
        label: 'Subscriptions',
        icon: RefreshCcwIcon,
      },
      settingsServiceTargetAreas: {
        key: 'settingsServiceTargetAreas',
        url: '/settings/service-target-areas',
        label: 'Target Areas',
        icon: TargetIcon,
        requiredModule: 'settings:asset-management',
      },
      settingsApplicationMethods: {
        key: 'settingsApplicationMethods',
        url: '/settings/application-methods',
        label: 'Application Methods',
        icon: SprayCanIcon,
        requiredModule: 'settings:asset-management',
      },
      settingsProducts: {
        key: 'settingsProducts',
        url: '/settings/products',
        label: 'Products',
        icon: FlaskConicalIcon,
        requiredModule: 'settings:asset-management',
      },
      settingsIntegrations: {
        key: 'settingsIntegrations',
        url: '/settings/integrations',
        label: 'Integrations',
        icon: BlocksIcon,
        requiredModule: 'settings:integrations',
      },
    },
  },
  admin: {
    key: 'admin',
    label: 'Admin',
    icon: LockIcon,
    requiredRole: 'superuser',
    items: {
      adminOrgs: {
        key: 'adminOrgs',
        label: 'Organizations',
        icon: Building2Icon,
        url: '/admin/orgs',
      },
      adminOffices: {
        key: 'adminOffices',
        label: 'Offices',
        icon: BuildingIcon,
        url: '/admin/offices',
      },
      adminEmployees: {
        key: 'adminEmployees',
        label: 'Employees',
        icon: User2Icon,
        url: '/admin/employees',
      },
      adminCalls: {
        key: 'adminCalls',
        label: 'Calls',
        icon: PhoneCallIcon,
        url: '/admin/calls',
      },
      adminAgent: {
        key: 'adminAgent',
        label: 'Agent',
        icon: BotIcon,
        url: '/admin/agent',
      },
      adminSeeding: {
        key: 'adminSeeding',
        label: 'Seeding',
        icon: DatabaseZapIcon,
        url: '/admin/seeding',
      },
      adminObservability: {
        key: 'adminObservability',
        label: 'Observability',
        icon: ActivitySquareIcon,
        url: '/admin/observability',
        requiredRole: 'superuser',
      },
      adminSimulator: {
        key: 'adminSimulator',
        label: 'Simulator',
        icon: FlaskConicalIcon,
        url: '/admin/simulator',
        requiredRole: 'superuser',
      },
      adminOfficeFlags: {
        key: 'adminOfficeFlags',
        label: 'Office Flags',
        icon: ToggleRightIcon,
        url: '/admin/office-flags',
        requiredRole: 'superuser',
      },
    },
  },
} satisfies Record<string, MenuItem>;
